// A leaf module (no imports): the redux slices read it without closing an import cycle.

// Optimistic seat-count adjustment used after a reservation is created (+1) or
// removed (-1), clamped so the count never goes below 0 or above seatsLimit.
// Single source for the cards and the meetings list reducer.
export const adjustSeatsUsed = (
  seatsUsed: number,
  seatsLimit: number | null | undefined,
  delta: 1 | -1
): number => {
  if (delta > 0) {
    // a zero limit means unlimited seats: nothing to clamp against
    if (seatsLimit === 0) return seatsUsed + 1;

    return seatsLimit != null ? Math.min(seatsUsed + 1, seatsLimit) : seatsUsed + 1;
  }

  return Math.max(seatsUsed - 1, 0);
};

export interface IReservationSeats {
  reservation: boolean;
  seatsLimit?: number | null;
  seatsUsed?: number | null;
  seatsLeft?: number | null;
}

// The student's own seat moves both counts, so a cancelled last seat is free again.
// A flag already in the target state leaves the counts alone.
export function withReservation<T extends IReservationSeats>(
  meeting: T,
  reserved: boolean
): T {
  if (meeting.reservation === reserved) return meeting;
  const delta = reserved ? 1 : -1;
  const next: T = { ...meeting, reservation: reserved };
  if (typeof meeting.seatsLeft === 'number') {
    next.seatsLeft = Math.max(meeting.seatsLeft - delta, 0);
  }
  if (typeof meeting.seatsUsed === 'number') {
    next.seatsUsed = adjustSeatsUsed(meeting.seatsUsed, meeting.seatsLimit, delta);
  }

  return next;
}
